import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from './apiClient';

/** What kind of record a hit is; the client maps each to an icon and the API supplies the route. */
export type SearchHitType =
  | 'kpi' | 'initiative' | 'milestone' | 'risk' | 'action' | 'decision' | 'meeting' | 'boardpack' | 'document' | 'strategy' | 'person' | 'note';

export interface SearchHit {
  id: string;
  type: SearchHitType;
  title: string;
  subtitle: string | null;
  code: string | null;
  /** Where the record opens in the client, relative to the app root; the API decides, so the client never guesses. */
  route: string;
}

export interface SearchGroup {
  type: SearchHitType;
  label: string;
  /** The list page with the term applied, where that page supports it; null when there is no such page. */
  moreRoute: string | null;
  /** The cap cut the list: there are more of this kind than shown. */
  hasMore: boolean;
  hits: SearchHit[];
}

export interface SearchResult {
  term: string;
  /** Only the kinds the caller may see and that matched; empty for a term shorter than the minimum. */
  groups: SearchGroup[];
}

/** Below this the API returns nothing and the client does not ask; mirrors GlobalSearchHandler.MinLength. */
export const SEARCH_MIN_LENGTH = 2;

/**
 * The term a "See all …" link from the search box carried to a list page, as `?q=`, for that page
 * to start filtered by. Read once when the page mounts (a lazy `useState` initialiser), so typing
 * in the page's own box afterwards is not fought by the URL.
 */
export function searchTermFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get('q') ?? '';
  } catch {
    return '';
  }
}

/**
 * GET /api/v1/search for a term. Kept fresh for half a minute so retyping the same thing is free,
 * and the previous answer stays on screen while the next one loads so the list does not blink.
 */
export function useGlobalSearch(term: string) {
  const q = term.trim();
  return useQuery({
    queryKey: ['search', q],
    queryFn: async () => (await apiClient.get<SearchResult>('/api/v1/search', { params: { q } })).data,
    enabled: q.length >= SEARCH_MIN_LENGTH,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
